import MeaPage from "../../../components/MeaPage";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Editor } from "primereact/editor";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import trainingsAPI from "../../../services/trainings";
import { useAuthContext } from "../../../hooks/useAuthContext";

const PAYMENT_METHODS_META = {
  cash: {
    label: "Espèces",
    description: "Paiement immédiat en espèces à l'école",
    icon: "pi pi-money-bill",
  },
  cb: {
    label: "CB",
    description: "Paiement par carte bancaire à l'école",
    icon: "pi pi-credit-card",
  },
  cheque: {
    label: "Chèque",
    description: "Paiement différé avec encaissement planifié à l'école",
    icon: "pi pi-wallet",
  },
  online: {
    label: "En ligne (HelloAsso)",
    description: "Paiement en ligne (HelloAsso) via l'espace famille",
    icon: "pi pi-globe",
  },
  other: {
    label: "Autres",
    description: "Méthode personnalisée",
    icon: "pi pi-ellipsis-h",
  },
};

const parseJSONSafely = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const DEFAULT_SETTINGS = {
  cache_enabled: true,
  cheque_enabled: true,
  cb_enabled: true,
  other_enabled: true,
  helloasso_enabled: false,
  helloasso_client_id: "",
  helloasso_organization_slug: "",
  helloasso_is_cach_in_compliant: null,
  helloasso_installments_options: [],
  helloasso_system_disabled: false,
};

const ContractsPaymentsSettingsPage = () => {
  const toast = useRef(null);
  const { auth } = useAuthContext();
  const accessToken = auth?.access_token || "";
  const params = useParams();
  const schoolId = params.schoolId;
  const registrationId = params.registrationId;

  const registrationQuery = trainingsAPI.GetOne(schoolId, registrationId, accessToken);
  const registration = registrationQuery?.data?.data;

  // Payment settings state (mirrors backend fields)
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  // Secret is never returned by GET (json:"-"), track separately
  const [clientSecret, setClientSecret] = useState("");
  const [secretModified, setSecretModified] = useState(false);
  // Track last saved HelloAsso config to detect unsaved changes
  const [savedHelloAssoConfig, setSavedHelloAssoConfig] = useState({
    client_id: "",
    organization_slug: "",
    secret_modified: false,
  });

  // Subscription section state
  const [calculateSubscriptionAmount, setCalculateSubscriptionAmount] = useState(false);
  const [subscriptionTable, setSubscriptionTable] = useState([]);
  const [subscriptionTerms, setSubscriptionTerms] = useState("");

  // Loading states
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
  const [savingSubscription, setSavingSubscription] = useState(false);
  const [verifyingCashIn, setVerifyingCashIn] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!schoolId || !registrationId) return;
    setLoadingSettings(true);
    try {
      const result = await trainingsAPI.GetPaymentSettings(schoolId, registrationId, accessToken);
      const data = result?.data || {};
      setSettings({
        cache_enabled: data.cache_enabled ?? true,
        cheque_enabled: data.cheque_enabled ?? true,
        cb_enabled: data.cb_enabled ?? true,
        other_enabled: data.other_enabled ?? true,
        helloasso_enabled: data.helloasso_enabled ?? false,
        helloasso_client_id: data.helloasso_client_id || "",
        helloasso_organization_slug: data.helloasso_organization_slug || "",
        helloasso_admin_url: data.helloasso_admin_url || "",
        helloasso_is_cach_in_compliant: data.helloasso_is_cach_in_compliant ?? null,
        helloasso_installments_options: data.helloasso_installments_options ?? [],
        helloasso_system_disabled: data.helloasso_system_disabled ?? false,
      });
      // Secret is never in response; reset modified flag
      setClientSecret("");
      setSecretModified(false);
      // Track saved HelloAsso config
      setSavedHelloAssoConfig({
        client_id: data.helloasso_client_id || "",
        organization_slug: data.helloasso_organization_slug || "",
        secret_modified: false,
      });
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Erreur",
        detail: "Impossible de charger les paramètres de paiement.",
        life: 3000,
      });
    } finally {
      setLoadingSettings(false);
    }
  }, [schoolId, registrationId, accessToken]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (!registration) return;
    setCalculateSubscriptionAmount(registration?.registration_calculate_subscription_amount || false);
    const mapping = parseJSONSafely(registration?.registration_subscription_amount_mapping, {});
    const normalizedTable = Object.entries(mapping).map(([, value]) => ({ value }));
    setSubscriptionTable(normalizedTable.length > 0 ? normalizedTable : [{ value: "" }]);
    setSubscriptionTerms(registration?.registration_subscription_terms || "");
  }, [registration]);

  const handleSavePaymentMethods = async () => {
    setSavingPaymentMethods(true);
    try {
      const patch = {
        cache_enabled: settings.cache_enabled,
        cheque_enabled: settings.cheque_enabled,
        cb_enabled: settings.cb_enabled,
        other_enabled: settings.other_enabled,
        helloasso_enabled: settings.helloasso_enabled,
        helloasso_client_id: settings.helloasso_client_id,
        helloasso_organization_slug: settings.helloasso_organization_slug,
        helloasso_installments_options: settings.helloasso_installments_options || [],
      };
      // Only send secret if user explicitly typed a new value
      if (secretModified && clientSecret) {
        patch.helloasso_client_secret = clientSecret;
      }
      await trainingsAPI.UpdatePaymentSettings(schoolId, registrationId, accessToken, patch);
      toast.current?.show({
        severity: "success",
        summary: "Succès",
        detail: "Paramètres de paiement enregistrés avec succès.",
        life: 2000,
      });
      await loadSettings();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Erreur",
        detail: "Impossible d'enregistrer les paramètres de paiement.",
        life: 3000,
      });
    } finally {
      setSavingPaymentMethods(false);
    }
  };

  const handleVerifyCashInCompliant = async () => {
    setVerifyingCashIn(true);
    try {
      const result = await trainingsAPI.CheckHelloAssoCompliance(schoolId, registrationId, accessToken);
      const compliant = result?.data?.compliant;
      await loadSettings();
      toast.current?.show({
        severity: compliant ? "success" : "warn",
        summary: "Vérification effectuée",
        detail: compliant
          ? "Votre organisation HelloAsso est conforme pour l'encaissement."
          : "Votre organisation HelloAsso n'est pas encore conforme pour l'encaissement.",
        life: 2500,
      });
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Erreur",
        detail: "Impossible de vérifier la conformité de réception des paiements HelloAsso.",
        life: 3000,
      });
    } finally {
      setVerifyingCashIn(false);
    }
  };

  const handleOpenHelloAssoVerification = () => {
    const url = `${settings.helloasso_admin_url}/${settings.helloasso_organization_slug}/verification`;
    window.open(url, "_blank");
  }

  const handleSaveCalculateSubscriptionAmount = async (value) => {
    setSavingSubscription(true);
    try {
      await trainingsAPI.UpdateOne(schoolId, registrationId, accessToken, registrationId, {
        registration_calculate_subscription_amount: value,
      });
      toast.current?.show({ severity: "success", summary: "Succès", detail: "Option de calcul automatique mise à jour.", life: 2000 });
    } catch {
      setCalculateSubscriptionAmount(!value);
      toast.current?.show({ severity: "error", summary: "Erreur", detail: "Impossible de mettre à jour l'option de calcul automatique.", life: 3000 });
    } finally {
      setSavingSubscription(false);
    }
  };

  const handleSaveSubscriptionTable = async () => {
    setSavingSubscription(true);
    try {
      await trainingsAPI.UpdateOne(schoolId, registrationId, accessToken, registrationId, {
        registration_subscription_amount_mapping: JSON.stringify(
          subscriptionTable.reduce((acc, item, index) => { acc[index + 1] = item.value; return acc; }, {})
        ),
        registration_subscription_terms: subscriptionTerms,
      });
      toast.current?.show({ severity: "success", summary: "Succès", detail: "Tableau de cotisation enregistré.", life: 2000 });
      registrationQuery.refetch();
    } catch {
      toast.current?.show({ severity: "error", summary: "Erreur", detail: "Impossible d'enregistrer le tableau de cotisation.", life: 3000 });
    } finally {
      setSavingSubscription(false);
    }
  };

  const handleCancelSubscriptionTable = () => {
    const mapping = parseJSONSafely(registration?.registration_subscription_amount_mapping, {});
    const normalizedTable = Object.entries(mapping).map(([, value]) => ({ value }));
    setSubscriptionTable(normalizedTable.length > 0 ? normalizedTable : [{ value: "" }]);
    setSubscriptionTerms(registration?.registration_subscription_terms || "");
  };

  const addSubscriptionLine = () => setSubscriptionTable((prev) => [...prev, { value: "" }]);
  const removeSubscriptionLine = (index) => setSubscriptionTable((prev) => prev.filter((_, i) => i !== index));
  const handleSubscriptionValueChange = (index, value) =>
    setSubscriptionTable((prev) => { const next = [...prev]; next[index] = { ...next[index], value }; return next; });

  const enabledMethodsCount = [
    settings.cache_enabled,
    settings.cb_enabled,
    settings.cheque_enabled,
    settings.helloasso_enabled,
    settings.other_enabled,
  ].filter(Boolean).length;

  const isHelloAssoConfigInvalid =
    settings.helloasso_enabled &&
    (!settings.helloasso_client_id || !settings.helloasso_organization_slug ||
      (!secretModified && !settings.helloasso_client_id)); // client_secret OK if not modified (already saved)

  const canSavePaymentMethods = !isHelloAssoConfigInvalid;

  // Check if there are unsaved HelloAsso changes
  const hasUnsavedHelloAssoChanges =
    settings.helloasso_client_id !== savedHelloAssoConfig.client_id ||
    settings.helloasso_organization_slug !== savedHelloAssoConfig.organization_slug ||
    secretModified;

  const getComplianceStatus = () => {
    const { helloasso_is_cach_in_compliant } = settings;
    if (helloasso_is_cach_in_compliant === null || helloasso_is_cach_in_compliant === undefined) {
      return { value: "Non vérifié", severity: "warning", helper: "Cliquez sur « Re-vérifier » pour contrôler la conformité de réception des paiements HelloAsso." };
    }
    if (!helloasso_is_cach_in_compliant) {
      return {
        value: "Non conforme pour l'encaissement",
        severity: "warning",
        helper: "Votre organisation HelloAsso n'est pas encore conforme pour l'encaissement. Merci de vérifier que votre configuration est correcte et que vous avez suivi les étapes de vérification auprès de HelloAsso.",
      };
    }
    return { value: "Conforme pour l'encaissement", severity: "success", helper: "Votre organisation HelloAsso est conforme pour l'encaissement." };
  };

  const complianceStatus = getComplianceStatus();

  const handleClientIdChange = useCallback((e) => {
    setSettings((s) => ({ ...s, helloasso_client_id: e.target.value }));
  }, []);

  const handleSecretChange = useCallback((e) => {
    setClientSecret(e.target.value);
    setSecretModified(true);
  }, []);

  const handleOrganizationSlugChange = useCallback((e) => {
    setSettings((s) => ({ ...s, helloasso_organization_slug: e.target.value }));
  }, []);

  const handleToggleCash = useCallback((v) => {
    setSettings((s) => ({ ...s, cache_enabled: v }));
  }, []);

  const handleToggleCB = useCallback((v) => {
    setSettings((s) => ({ ...s, cb_enabled: v }));
  }, []);

  const handleToggleCheque = useCallback((v) => {
    setSettings((s) => ({ ...s, cheque_enabled: v }));
  }, []);

  const handleToggleHelloAsso = useCallback((v) => {
    setSettings((s) => ({ ...s, helloasso_enabled: v }));
  }, []);

  const handleToggleOther = useCallback((v) => {
    setSettings((s) => ({ ...s, other_enabled: v }));
  }, []);

  const MethodCard = useCallback(({ metaKey, enabled, onToggle, disabled = false, children }) => (
    <div className={`h-full border-1 border-round-xl p-3 ${enabled ? "border-primary surface-50" : "surface-border"}`}>
      <div className="flex align-items-start justify-content-between gap-3">
        <div className="flex gap-3">
          <div className="flex align-items-center justify-content-center border-circle surface-200" style={{ width: "2.5rem", height: "2.5rem" }}>
            <i className={`${PAYMENT_METHODS_META[metaKey].icon} text-900`}></i>
          </div>
          <div>
            <div className="font-semibold text-900">{PAYMENT_METHODS_META[metaKey].label}</div>
            <small className="text-600">{PAYMENT_METHODS_META[metaKey].description}</small>
          </div>
        </div>
        <div className="flex flex-column align-items-end gap-2">
          <small className={`font-medium ${enabled ? "text-green-600" : "text-500"}`}>
            {enabled ? "Actif" : "Inactif"}
          </small>
          <InputSwitch checked={enabled} onChange={(e) => onToggle(e.value)} disabled={disabled} />
        </div>
      </div>
      {children}
    </div>
  ), []);

  return (
    <>
      <Toast ref={toast} position="bottom-center" />
      <MeaPage
        title="Paramètres des paiements"
        subtitle="Configuration des méthodes de paiement et des règles de cotisation"
        showBackButton={true}
      >
        <div className="grid">
          <div className="col-12">
            <Card title="Méthodes de paiement">
              {loadingSettings ? (
                <div className="grid">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="col-12 md:col-6 xl:col-4">
                      <div className="border-1 surface-border border-round-xl p-3">
                        <div className="flex align-items-center justify-content-between gap-3">
                          <div className="flex gap-3 align-items-center">
                            <Skeleton shape="circle" size="2.5rem" />
                            <div>
                              <Skeleton width="8rem" height="1rem" className="mb-2" />
                              <Skeleton width="12rem" height="0.75rem" />
                            </div>
                          </div>
                          <Skeleton width="3rem" height="1.5rem" borderRadius="1rem" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <>
              <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2 mb-4">
                <div className="text-700">Activez uniquement les méthodes autorisées pour cette formation.</div>
                <div className="text-sm font-medium text-900">{enabledMethodsCount}/5 méthode(s) active(s)</div>
              </div>

              {isHelloAssoConfigInvalid && (
                <div className="p-3 border-1 border-red-200 border-round bg-red-50 text-red-700 mb-4">
                  Complétez la configuration HelloAsso (ClientID, ClientSecret, OrganizationSlug) avant d'enregistrer.
                </div>
              )}

              <div className="grid">
                <div className="col-12 md:col-6 xl:col-4">
                  <MethodCard
                    metaKey="cash"
                    enabled={settings.cache_enabled}
                    onToggle={handleToggleCash}
                  />
                </div>

                <div className="col-12 md:col-6 xl:col-4">
                  <MethodCard
                    metaKey="cb"
                    enabled={settings.cb_enabled}
                    onToggle={handleToggleCB}
                  />
                </div>

                <div className="col-12 md:col-6 xl:col-4">
                  <MethodCard
                    metaKey="cheque"
                    enabled={settings.cheque_enabled}
                    onToggle={handleToggleCheque}
                  />
                </div>

                <div className="col-12 md:col-6 xl:col-8">
                  <MethodCard
                    metaKey="online"
                    enabled={settings.helloasso_enabled}
                    onToggle={handleToggleHelloAsso}
                    disabled={settings.helloasso_system_disabled}
                  >
                    {settings.helloasso_system_disabled && (
                      <div className="p-3 border-1 border-orange-200 border-round bg-orange-50 text-orange-700 mt-3 flex align-items-start gap-2">
                        <i className="pi pi-exclamation-triangle mt-1"></i>
                        <div>
                          Le paiement par HelloAsso est actuellement désactivé pour toute la plateforme par l'administration système. Vos configurations restent enregistrées, mais les familles ne peuvent pas effectuer de paiements en ligne pour le moment.
                        </div>
                      </div>
                    )}
                    {settings.helloasso_enabled && (
                      <div className="mt-3 grid">
                        <div className="col-12">
                          <div className="p-3 surface-100 border-round flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
                            <div className="font-medium text-900 flex align-items-center">Recevoir des paiements <img src="/images/logo-helloasso-150.png" alt="HelloAsso" className="ml-2" /></div>
                            <Tag value={complianceStatus.value} severity={complianceStatus.severity} />
                          </div>
                          <small className="text-600 mt-2 block">{complianceStatus.helper}</small>
                          <div className="mt-3">
                            <div>
                              <Button
                                label="Re-vérifier la conformité HelloAsso"
                                icon="pi pi-sync"
                                outlined
                                onClick={handleVerifyCashInCompliant}
                                loading={verifyingCashIn}
                                disabled={
                                  hasUnsavedHelloAssoChanges ||
                                  !settings.helloasso_client_id ||
                                  !settings.helloasso_organization_slug
                                }
                                title={
                                  hasUnsavedHelloAssoChanges
                                    ? "Enregistrez d'abord les modifications de configuration HelloAsso"
                                    : !settings.helloasso_client_id || !settings.helloasso_organization_slug
                                    ? "Complétez ClientID et OrganizationSlug avant de vérifier"
                                    : ""
                                }
                              />
                              <Button
                                label="Ouvrir son espace de vérification HelloAsso"
                                icon="pi pi-external-link"
                                className="ml-2"
                                outlined
                                onClick={handleOpenHelloAssoVerification}
                                loading={verifyingCashIn}
                                disabled={                                  
                                  hasUnsavedHelloAssoChanges ||
                                  !settings.helloasso_admin_url ||
                                  !settings.helloasso_client_id ||
                                  !settings.helloasso_organization_slug
                                }
                                title={
                                  hasUnsavedHelloAssoChanges
                                    ? "Enregistrez d'abord les modifications de configuration HelloAsso"
                                    : !settings.helloasso_client_id || !settings.helloasso_organization_slug
                                    ? "Complétez ClientID et OrganizationSlug avant de vérifier"
                                    : ""
                                }
                              />
                            </div>
                            {(hasUnsavedHelloAssoChanges ||
                              !settings.helloasso_client_id ||
                              !settings.helloasso_organization_slug) && (
                              <small className="text-orange-600 mt-2 block">
                                <i className="pi pi-exclamation-circle mr-2" />
                                {hasUnsavedHelloAssoChanges
                                  ? "Enregistrez les modifications HelloAsso avant de vérifier"
                                  : "Complétez la configuration HelloAsso (ClientID, OrganizationSlug)"}
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="col-12 md:col-6">
                          <label className="block mb-2">ClientID</label>
                          <InputText
                            value={settings.helloasso_client_id}
                            onChange={handleClientIdChange}
                            className="w-full"
                            placeholder="helloasso_client_id"
                          />
                        </div>

                        <div className="col-12 md:col-6">
                          <label className="block mb-2">
                            ClientSecret
                            {!secretModified && <small className="text-500 ml-2">(laisser vide pour ne pas modifier)</small>}
                          </label>
                          <Password
                            value={clientSecret}
                            onChange={handleSecretChange}
                            className="w-full"
                            inputClassName="w-full"
                            placeholder={secretModified ? "" : "••••••••"}
                            feedback={false}
                            toggleMask
                          />
                        </div>

                        <div className="col-12 md:col-6">
                          <label className="block mb-2">OrganizationSlug</label>
                          <InputText
                            value={settings.helloasso_organization_slug}
                            onChange={handleOrganizationSlugChange}
                            className="w-full"
                            placeholder="mon-organisation"
                          />
                        </div>

                        <div className="col-12">
                          <label className="block mb-2 font-medium">Options de paiement fractionné autorisées</label>
                          <div className="flex flex-wrap gap-4 mt-2">
                            {[2, 3, 4, 5, 6].map((opt) => (
                              <div key={opt} className="flex align-items-center gap-2">
                                <Checkbox
                                  inputId={`opt-${opt}`}
                                  checked={settings.helloasso_installments_options?.includes(opt)}
                                  onChange={(e) => {
                                    setSettings((s) => {
                                      const current = s.helloasso_installments_options || [];
                                      const next = e.checked
                                        ? [...current, opt].sort()
                                        : current.filter((x) => x !== opt);
                                      return { ...s, helloasso_installments_options: next };
                                    });
                                  }}
                                />
                                <label htmlFor={`opt-${opt}`}>Paiement en {opt}X</label>
                              </div>
                            ))}
                          </div>
                          <small className="text-600 block mt-2">
                            Les familles pourront choisir parmi les options cochées, en plus du paiement en 1 fois (toujours disponible).
                          </small>
                        </div>
                      </div>
                    )}
                  </MethodCard>
                </div>

                <div className="col-12 md:col-6 xl:col-4">
                  <MethodCard
                    metaKey="other"
                    enabled={settings.other_enabled}
                    onToggle={handleToggleOther}
                  />
                </div>
              </div>

              <div className="flex justify-content-end mt-3">
                <Button
                  label="Enregistrer"
                  icon="pi pi-save"
                  onClick={handleSavePaymentMethods}
                  loading={savingPaymentMethods}
                  disabled={!canSavePaymentMethods}
                />
              </div>
              </>
              )}
            </Card>
          </div>

          <div className="col-12">
            <Card title="Calcul automatique des montants de la cotisation">
              {loadingSettings ? (
                <div className="flex flex-column gap-3">
                  <div className="flex align-items-center gap-3">
                    <Skeleton width="14rem" height="1rem" />
                    <Skeleton width="3rem" height="1.5rem" borderRadius="1rem" />
                  </div>
                </div>
              ) : (
              <>
              <div className="field">
                <label className="w-12rem pr-3">Activer le calcul automatique</label>
                <div className="mt-2">
                  <InputSwitch
                    checked={calculateSubscriptionAmount}
                    onChange={(e) => {
                      setCalculateSubscriptionAmount(e.value);
                      handleSaveCalculateSubscriptionAmount(e.value);
                    }}
                    disabled={savingSubscription}
                  />
                </div>
              </div>

              {calculateSubscriptionAmount && (
                <div className="mt-3">
                  <label className="block text-900 font-medium mb-2">Tableau de cotisation</label>
                  {subscriptionTable.map((item, index) => (
                    <div key={index} className="flex align-items-center mb-2">
                      <span className="mr-2">{index + 1} élève(s)</span>
                      <InputNumber
                        value={item.value}
                        onValueChange={(e) => handleSubscriptionValueChange(index, e.value ?? "")}
                        className="w-14rem"
                        min={0}
                      />
                      {subscriptionTable.length > 1 && (
                        <Button
                          icon="pi pi-minus"
                          className="p-button-rounded p-button-danger ml-2"
                          onClick={() => removeSubscriptionLine(index)}
                        />
                      )}
                    </div>
                  ))}

                  <Button
                    icon="pi pi-plus"
                    className="p-button-rounded p-button-success mt-2"
                    onClick={addSubscriptionLine}
                  />

                  <div className="mt-4">
                    <label htmlFor="subscriptionTerms" className="block text-900 font-medium mb-2">
                      Conditions de cotisation
                    </label>
                    <small className="block text-gray-600 mb-2">
                      Texte explicatif des conditions de cotisation qui sera affiché aux parents.
                    </small>
                    <Editor
                      id="subscriptionTerms"
                      value={subscriptionTerms}
                      onTextChange={(e) => {
                        setSubscriptionTerms(e.htmlValue || "");
                      }}
                      style={{ height: "200px" }}
                    />
                  </div>

                  <div className="flex justify-content-end mt-3 gap-2">
                    <Button
                      className="p-button p-button-primary"
                      onClick={handleSaveSubscriptionTable}
                      disabled={subscriptionTable.some((item) => item.value === "")}
                      loading={savingSubscription}
                    >
                      Enregistrer
                    </Button>
                    <Button outlined onClick={handleCancelSubscriptionTable}>
                      Annuler
                    </Button>
                  </div>
                </div>
              )}
              </>
              )}
            </Card>
          </div>
        </div>
      </MeaPage>
    </>
  );
};

export default ContractsPaymentsSettingsPage;
